import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { useTheme } from '../utils/ThemeContext';
import Modal from './Modal';

/**
 * Profile settings modal that allows users to update their display name and theme preference
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Object} props.user - Current user object from Firebase Auth
 * @returns {JSX.Element} Rendered ProfileModal component
 */
const ProfileModal = ({ isOpen, onClose, user }) => {
  const { theme, toggleTheme } = useTheme();
  const [displayName, setDisplayName] = useState('');
  const [themePreference, setThemePreference] = useState(theme);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  
  // Image upload states
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previousPhoto, setPreviousPhoto] = useState(null);
  const [pendingPhotoUrl, setPendingPhotoUrl] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [imageStatus, setImageStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'

  // Initialize form with current user data
  useEffect(() => {
    if (user && isOpen) {
      setDisplayName(user.displayName || user.email.split('@')[0]);
      
      // Get user preferences from Firestore
      const fetchUserPreferences = async () => {
        try {
          const userPrefsRef = doc(db, "userPreferences", user.uid);
          const userPrefsSnap = await getDoc(userPrefsRef);
          
          if (userPrefsSnap.exists()) {
            const prefs = userPrefsSnap.data();
            if (prefs.theme) {
              setThemePreference(prefs.theme);
            } else {
              // If theme doesn't exist in Firestore but exists in localStorage, use localStorage value
              setThemePreference(localStorage.getItem('theme') || theme);
            }
            
            // Set profile photo URL if available in preferences
            if (prefs.profilePhotoUrl) {
              setProfilePhotoUrl(prefs.profilePhotoUrl);
            } else if (user.photoURL) {
              // If no custom photo but user has Google photo, use that
              setProfilePhotoUrl(user.photoURL);
            }
          } else {
            // If no prefs in Firestore, use localStorage value for theme
            setThemePreference(localStorage.getItem('theme') || theme);
            
            // If user has Google photo, use that
            if (user.photoURL) {
              setProfilePhotoUrl(user.photoURL);
            }
          }
        } catch (error) {
          console.error('Error fetching user preferences:', error);
          setThemePreference(localStorage.getItem('theme') || theme);
          
          // If user has Google photo, use that
          if (user.photoURL) {
            setProfilePhotoUrl(user.photoURL);
          }
        }
      };
      
      fetchUserPreferences();
    }
  }, [user, theme, isOpen]);
  
  // Effect to check if the resized image is available
  useEffect(() => {
    if (!pendingPhotoUrl) return;
    
    const MAX_RETRIES = 10;
    const RETRY_DELAY = 2000; // 2 seconds between retries
    
    if (retryCount >= MAX_RETRIES) {
      console.error("Max retries reached while waiting for resized image");
      setImageStatus('error');
      // Use the original upload URL as fallback
      setProfilePhotoUrl(pendingPhotoUrl);
      setPendingPhotoUrl(null);
      setRetryCount(0);
      setIsUploading(false);
      return;
    }

    setImageStatus('loading');
    
    // Check if the image exists
    const checkImage = () => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = pendingPhotoUrl;
      });
    };
    
    const timer = setTimeout(async () => {
      const imageExists = await checkImage();
      
      if (imageExists) {
        setProfilePhotoUrl(pendingPhotoUrl);
        setImageStatus('success');
        setPendingPhotoUrl(null);
        setRetryCount(0);
        setIsUploading(false);
      } else {
        console.log(`Resized image not ready yet, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
        setRetryCount(prevCount => prevCount + 1);
      }
    }, RETRY_DELAY);
    
    return () => clearTimeout(timer);
  }, [pendingPhotoUrl, retryCount]);

  // Effect to animate the progress bar during file upload
  useEffect(() => {
    let progressInterval;
    
    if (isUploading && uploadProgress !== null && uploadProgress < 90) {
      progressInterval = setInterval(() => {
        setUploadProgress(prevProgress => {
          // Simulate progress that moves faster at the beginning and slows down as it approaches 90%
          const increment = Math.max(1, 10 - Math.floor(prevProgress / 10));
          const nextProgress = prevProgress + increment;
          return nextProgress < 90 ? nextProgress : 90;
        });
      }, 300);
    }
    
    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isUploading, uploadProgress]);

  /**
   * Save user preferences to Firestore
   * 
   * @param {string} newTheme - The new theme preference to save
   * @param {string} photoUrl - The profile photo URL to save
   */
  const saveUserPreferences = async (newTheme, photoUrl) => {
    if (!user) return;
    
    try {
      const userPrefsRef = doc(db, "userPreferences", user.uid);
      await setDoc(userPrefsRef, {
        theme: newTheme,
        profilePhotoUrl: photoUrl || null,
        updatedAt: Timestamp.now()
      }, { merge: true }); // Use merge to avoid overwriting other preferences
      
      console.log('User preferences saved to Firestore');
    } catch (error) {
      console.error('Error saving user preferences:', error);
      throw error;
    }
  };

  /**
   * Transform Firebase Storage URL to use the avatar version
   * 
   * @param {string} url - Original Firebase Storage URL
   * @returns {string} URL with _resized_avatar added before the file extension
   */
  const getAvatarImageUrl = (url) => {
    if (!url) return url;
    
    // If it's not a Firebase Storage URL (e.g., Google profile photo), return as is
    if (!url.includes('firebasestorage.googleapis.com')) return url;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return url;
    
    const encodedFilename = filenameMatch[1]; // This is URL encoded
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Remove any existing resize suffix 
    const baseFilename = decodedFilename.replace(/_resized_(medium|thumb|avatar)/g, '');
    
    // Extract file extension and directory
    const lastDotIndex = baseFilename.lastIndexOf('.');
    if (lastDotIndex === -1) return url; // No extension found
    
    const lastSlashIndex = baseFilename.lastIndexOf('/');
    const directory = lastSlashIndex !== -1 ? baseFilename.substring(0, lastSlashIndex + 1) : '';
    const filename = lastSlashIndex !== -1 ? baseFilename.substring(lastSlashIndex + 1) : baseFilename;
    
    const filenameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const extension = filename.substring(filename.lastIndexOf('.'));
    
    // Create new filename with _resized_avatar suffix
    const newFilename = `${directory}${filenameWithoutExt}_resized_avatar${extension}`;
    const encodedNewFilename = encodeURIComponent(newFilename);
    
    // Replace the filename in the URL
    return url.replace(encodedFilename, encodedNewFilename);
  };

  /**
   * Extract the base filename from a Firebase Storage URL
   * 
   * @param {string} url - Firebase Storage URL
   * @returns {string|null} Base filename without resize suffix or null if can't parse
   */
  const getBaseFilenameFromUrl = (url) => {
    if (!url) return null;
    
    // If it's not a Firebase Storage URL, return null
    if (!url.includes('firebasestorage.googleapis.com')) return null;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return null;
    
    const encodedFilename = filenameMatch[1];
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Remove any resize suffix from the filename
    return decodedFilename.replace(/_resized_(medium|thumb|avatar)/g, '');
  };

  /**
   * Delete old image files from storage
   * 
   * @param {string} url - URL of the image to delete
   * @returns {Promise<void>}
   */
  const deleteOldImages = async (url) => {
    if (!url) return;
    
    // Skip deleting if it's not a Firebase Storage URL (e.g., Google profile URL)
    if (!url.includes('firebasestorage.googleapis.com')) return;
    
    try {
      const baseFilename = getBaseFilenameFromUrl(url);
      if (!baseFilename) {
        console.log('Error: Could not extract base filename from URL:', url);
        return;
      }
      
      console.log('Attempting to delete images with base filename:', baseFilename);
      const fileDir = baseFilename.includes('/') ? baseFilename.substring(0, baseFilename.lastIndexOf('/')) : '';
      const fileName = baseFilename.includes('/') ? baseFilename.substring(baseFilename.lastIndexOf('/') + 1) : baseFilename;
      const fileExt = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      
      // Create reference to the avatar version
      const avatarRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_avatar${fileExt}`);
      
      // Delete the file
      await deleteObject(avatarRef).catch(err => console.log('Avatar file may not exist:', err));
    } catch (error) {
      console.error('Error deleting old images:', error);
      // Don't throw the error - we don't want to interrupt the upload process
    }
  };

  /**
   * Get image dimensions
   * 
   * @param {File} file - Image file
   * @returns {Promise<{width: number, height: number}>} Image dimensions
   */
  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
        URL.revokeObjectURL(img.src); // Clean up
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
        URL.revokeObjectURL(img.src); // Clean up
      };
      img.src = URL.createObjectURL(file);
    });
  };

  /**
   * Handle file upload for profile photo
   * 
   * @param {Event} e - Change event
   */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type - strictly JPG, PNG, WebP only
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/webp'
    ];
    const allowedExtensions = /\.(jpg|jpeg|png|webp)$/i;
    
    // Check both MIME type and file extension
    if (!allowedTypes.includes(file.type) || !allowedExtensions.test(file.name)) {
      alert('Please select a PNG, JPG, or WebP image file.');
      e.target.value = ''; // Clear the file input
      return;
    }
    
    // Check file size (2MB limit for profile photos)
    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      alert('File is too large. Please choose an image under 2MB.');
      e.target.value = ''; // Clear the file input
      return;
    }
    
    // Check image dimensions before upload
    try {
      const dimensions = await getImageDimensions(file);
      const MIN_WIDTH = 150;
      const MIN_HEIGHT = 150;
      
      if (dimensions.width < MIN_WIDTH || dimensions.height < MIN_HEIGHT) {
        alert(`Image is too small (${dimensions.width}x${dimensions.height}). Minimum required: ${MIN_WIDTH}x${MIN_HEIGHT} pixels.`);
        e.target.value = ''; // Clear the file input
        return;
      }
    } catch (error) {
      console.error("Error checking image dimensions:", error);
      // If we can't check dimensions, let the server validate it
    }
    
    try {
      // Store the previous photo URL before starting upload
      const previousPhotoUrl = profilePhotoUrl;
      setPreviousPhoto(previousPhotoUrl);
      
      // Clear current photo during upload to avoid showing old image
      setProfilePhotoUrl('');
      setIsUploading(true);
      setUploadProgress(0);
      
      // Delete old images if they exist (only if it's our own Firebase Storage URL, not Google's)
      if (previousPhotoUrl && previousPhotoUrl.includes('firebasestorage.googleapis.com')) {
        await deleteOldImages(previousPhotoUrl);
      }
      
      // Start the progress animation
      const storageRef = ref(storage, `profile-photos/${user.uid}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      
      // Set to 100% when upload is complete
      setUploadProgress(100);
      
      // Slight delay to show 100% before moving to the next phase
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const downloadURL = await getDownloadURL(storageRef);
      
      // Transform URL to use the avatar version
      const avatarURL = getAvatarImageUrl(downloadURL);
      
      // Instead of setting it immediately, set it as pending and let the effect handle it
      setPendingPhotoUrl(avatarURL);
      setUploadProgress(null);
      // Note: isUploading remains true until the effect confirms the image is loaded
    } catch (error) {
      console.error("Error uploading file:", error);
      // Restore previous photo if upload fails
      setProfilePhotoUrl(previousPhoto || '');
      setPreviousPhoto(null);
      setUploadProgress(null);
      setIsUploading(false);
      e.target.value = ''; // Clear the file input
    }
  };

  /**
   * Handle saving user profile settings
   */
  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Update display name in Firebase Auth
      await updateProfile(auth.currentUser, {
        displayName: displayName.trim() || user.email.split('@')[0],
        // Only update photoURL in Firebase Auth if it's our custom photo (not Google's)
        photoURL: profilePhotoUrl && profilePhotoUrl.includes('profile-photos') ? profilePhotoUrl : user.photoURL
      });

      // Update theme preference and profile photo in Firestore
      await saveUserPreferences(themePreference, profilePhotoUrl);
      
      // Update theme if current theme doesn't match preference
      if (themePreference !== theme) {
        // Toggle theme to match preference
        toggleTheme();
      }

      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        onClose(); // Close modal after a short delay
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Remove profile photo and restore to default
   */
  const handleRemovePhoto = async () => {
    // Only process if there is a photo to remove and it's our custom photo, not Google's
    if (profilePhotoUrl && profilePhotoUrl.includes('profile-photos')) {
      try {
        // Delete the image from storage
        await deleteOldImages(profilePhotoUrl);
        
        // Update the state
        setProfilePhotoUrl('');
        
        // If user has a Google photo, revert to that
        if (user.photoURL && !user.photoURL.includes('profile-photos')) {
          setProfilePhotoUrl(user.photoURL);
        }
      } catch (error) {
        console.error('Error removing profile photo:', error);
        setError('Failed to remove profile photo. Please try again.');
      }
    } else if (profilePhotoUrl && !profilePhotoUrl.includes('profile-photos')) {
      // If using Google photo, just clear our custom photo preference
      setProfilePhotoUrl('');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Profile Settings">
      <Form onSubmit={handleSave}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form.Group className="mb-4">
          <Form.Label>Profile Photo</Form.Label>
          <div className="d-flex align-items-center mb-3">
            {profilePhotoUrl ? (
              <img 
                src={profilePhotoUrl} 
                alt="Profile" 
                className="rounded-circle" 
                style={{ width: '100px', height: '100px', objectFit: 'cover' }} 
              />
            ) : (
              <div 
                className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                style={{ width: '100px', height: '100px' }}
              >
                <i className="bi bi-person" style={{ fontSize: '3rem' }}></i>
              </div>
            )}
            <div className="ms-3">
              <Form.Control
                type="file"
                accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                onChange={handleFileUpload}
                size="sm"
                className="mb-2"
              />
              <div>
                <small className="text-muted">PNG, JPG, or WebP (max 2MB)</small>
              </div>
              {profilePhotoUrl && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRemovePhoto}
                  className="mt-2"
                >
                  Remove Photo
                </Button>
              )}
            </div>
          </div>
          
          {isUploading && (
            <div className="upload-status mt-2">
              {uploadProgress !== null && (
                <div>
                  <small className="text-muted mb-1 d-block">
                    Uploading... {uploadProgress}%
                  </small>
                  <ProgressBar 
                    now={uploadProgress} 
                    variant="primary" 
                    animated 
                    style={{ height: '8px' }} 
                  />
                </div>
              )}
              
              {uploadProgress === null && imageStatus === 'loading' && (
                <div className="d-flex align-items-center gap-2 mt-1">
                  <small className="text-muted">
                    Processing image... (attempt {retryCount + 1})
                  </small>
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              
              {uploadProgress === null && imageStatus !== 'loading' && (
                <div className="d-flex align-items-center gap-2 mt-1">
                  <small className="text-muted">
                    Processing image...
                  </small>
                  <Spinner animation="border" size="sm" />
                </div>
              )}
            </div>
          )}
          
          {imageStatus === 'error' && (
            <Alert variant="warning" className="mt-2 py-2 px-3">
              The resized image could not be loaded. Using original image instead.
            </Alert>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Display Name</Form.Label>
          <Form.Control
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your display name"
          />
          <Form.Text className="text-muted">
            This name will be displayed in the header. Leave blank to use your email username.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Theme Preference</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label="Light"
              name="themePreference"
              id="light-theme"
              checked={themePreference === 'light'}
              onChange={() => setThemePreference('light')}
            />
            <Form.Check
              inline
              type="radio"
              label="Dark"
              name="themePreference"
              id="dark-theme"
              checked={themePreference === 'dark'}
              onChange={() => setThemePreference('dark')}
            />
          </div>
          <Form.Text className="text-muted">
            Select your preferred theme for the application. This will be applied across all your devices.
          </Form.Text>
        </Form.Group>

        <Row className="mt-4">
          <Col>
            <Button variant="secondary" onClick={onClose} className="me-2">
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={isLoading || isUploading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ProfileModal;